<template>
  <div class="guest-order-lookup" data-testid="guest-order-lookup">
    <h2 class="guest-order-lookup__title">Find your order</h2>

    <p v-if="!submitted" class="guest-order-lookup__intro">
      Checked out as a guest? Enter the email you used and we'll send you a
      link to your order.
    </p>

    <!--
      SECURITY (card tmMqkxM1): once submitted we show ONE confirmation,
      identical whether or not the address matched an order, and identical
      when the request fails. Anything that distinguished those cases would
      turn this form into a membership oracle for customer emails -- the
      exact leak the backend's uniform 202 is designed to prevent.
    -->
    <p
      v-if="submitted"
      class="guest-order-lookup__sent"
      data-testid="guest-lookup-sent"
    >
      If we found orders for that email address, we've sent the order links to
      it. Please check your inbox.
    </p>

    <form
      v-else
      class="guest-order-lookup__form"
      data-testid="guest-lookup-submit"
      @submit.prevent="onSubmit"
    >
      <label class="guest-order-lookup__label" for="guest-lookup-email">
        Email address
      </label>
      <input
        id="guest-lookup-email"
        v-model="email"
        type="email"
        class="guest-order-lookup__input"
        placeholder="you@example.com"
        data-testid="guest-lookup-email"
        :disabled="submitting"
      />
      <button
        type="submit"
        class="guest-order-lookup__button"
        :disabled="submitting || !email.trim()"
        data-testid="guest-lookup-button"
      >
        {{ submitting ? "Sending..." : "Email my order link" }}
      </button>
    </form>
  </div>
</template>

<script>
/**
 * "Email me my order link" -- the guest's way back to an order.
 *
 * Card: https://trello.com/c/tmMqkxM1
 * Spec: docs/planning/2026-08-10-users-profile-orders-auth-spec.md (Feature 3).
 *
 * Guest checkout is the DEFAULT, so many shoppers have no account and cannot
 * sign in to reach order history. They ask for their link by email; the
 * system mails it and shows NOTHING on screen.
 */
export default {
  name: "StorefrontGuestOrderLookup",
  data() {
    return {
      email: "",
      submitting: false,
      submitted: false,
    };
  },
  methods: {
    async onSubmit() {
      const email = (this.email || "").trim();
      if (!email || this.submitting) {
        return;
      }
      this.submitting = true;
      try {
        await this.$axios.post("/guest-orders/lookup", { email });
      } catch (err) {
        // Deliberately swallowed. A visible error state would differ from
        // the success state and re-open the enumeration oracle; the user
        // sees the same confirmation either way.
        // eslint-disable-next-line no-console
        console.debug("guest order lookup request failed");
      } finally {
        this.submitting = false;
        // Set LAST and unconditionally, so success and failure land in the
        // identical UI state.
        this.submitted = true;
      }
    },
  },
};
</script>

<style scoped>
.guest-order-lookup {
  max-width: 32rem;
}

.guest-order-lookup__title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.guest-order-lookup__intro,
.guest-order-lookup__sent {
  margin-bottom: 1rem;
}

.guest-order-lookup__form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.guest-order-lookup__label {
  font-size: 0.875rem;
  font-weight: 500;
}

.guest-order-lookup__input {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0.5rem;
}

.guest-order-lookup__button {
  align-self: flex-start;
  background: #1976d2;
  border-radius: 4px;
  color: #fff;
  padding: 0.5rem 1rem;
}

.guest-order-lookup__button:disabled {
  opacity: 0.6;
}
</style>
